import { Button, IconButton, Modal } from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Eye } from 'lucide-react';
import { useState } from 'react';
import { getApiError, recordsApi } from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import { ErrorCard, LoadingCard } from '../../app/data-view.js';
import { HealthPanel } from './health-panel.js';
import { chartQueryKey, cn } from './helpers.js';

/// O ícone do Prontuário na linha da lista de clientes (ADR 0038, fatia 1). Todos os ícones da
/// página leem a mesma contagem numa consulta só: a chave é a lista de clientes da página.
export function ClientRecordsButton({
  clientId,
  clientName,
  pageClientIds,
  timeZone,
  onOpenChart,
  canManage = false,
}: {
  clientId: string;
  clientName: string;
  pageClientIds: readonly string[];
  timeZone: string;
  /// Vai à aba Prontuário da página do cliente, com a ficha aberta quando houver.
  /// Vai à aba Prontuário; com modelo, abre a ficha, e com `edit` já na edição.
  onOpenChart: (templateId?: string, edit?: boolean) => void;
  /// Quem preenche fichas vê, na divergência, o atalho para atualizar a ficha que negou.
  canManage?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const counts = useQuery({
    queryKey: ['company', 'records', 'health-counts', pageClientIds],
    queryFn: () => recordsApi.healthCounts(pageClientIds),
    retry: false,
  });
  const count = counts.data?.find((item) => item.clientId === clientId);
  const records = count?.records ?? 0;
  const points = count?.points ?? 0;
  const label = records
    ? `${String(records)} ${records === 1 ? 'ficha' : 'fichas'} · ${String(points)} ${
        points === 1 ? 'ponto' : 'pontos'
      } de saúde`
    : 'Nenhuma ficha preenchida';
  return (
    <>
      <IconButton
        label={`Ver prontuário de ${clientName}: ${label}`}
        onClick={() => setOpen(true)}
        className={cn('relative', records ? 'text-brand-400' : '')}
        icon={
          <>
            <ClipboardList />
            {points ? (
              <span
                className={cn(
                  'absolute -right-1 -top-1 grid min-w-4 place-items-center rounded-full border bg-surface-card px-1 text-[10px] font-bold leading-4',
                  count?.divergences ? 'border-warning text-warning' : 'border-danger text-danger',
                )}
              >
                {points}
              </span>
            ) : null}
          </>
        }
      />
      {open ? (
        <ClientChartDialog
          clientId={clientId}
          clientName={clientName}
          timeZone={timeZone}
          onClose={() => setOpen(false)}
          onOpenChart={onOpenChart}
          canManage={canManage}
        />
      ) : null}
    </>
  );
}

function ClientChartDialog({
  clientId,
  clientName,
  timeZone,
  onClose,
  onOpenChart,
  canManage = false,
}: {
  clientId: string;
  clientName: string;
  timeZone: string;
  onClose: () => void;
  /// Vai à aba Prontuário; com modelo, abre a ficha, e com `edit` já na edição.
  onOpenChart: (templateId?: string, edit?: boolean) => void;
  /// Quem preenche fichas vê, na divergência, o atalho para atualizar a ficha que negou.
  canManage?: boolean;
}) {
  const chart = useQuery({
    queryKey: chartQueryKey(clientId),
    queryFn: () => recordsApi.getChart(clientId),
    retry: false,
  });
  return (
    <Modal
      open
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
      title={clientName}
      description="Prontuário · somente leitura"
      footer={<Button onClick={() => onOpenChart()}>Abrir prontuário</Button>}
    >
      {chart.isLoading ? (
        <LoadingCard label="Carregando prontuário" />
      ) : chart.error || !chart.data ? (
        <ErrorCard message={getApiError(chart.error)} />
      ) : (
        <div className="grid gap-5">
          <HealthPanel
            chart={chart.data}
            timeZone={timeZone}
            compact
            canManage={canManage}
            onOpenRecord={(templateId, edit) => onOpenChart(templateId, edit)}
          />
          <section className="grid gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-content-faint">
              Fichas
            </h3>
            {chart.data.records.length ? (
              [...chart.data.records]
                .sort(
                  (left, right) =>
                    chart.data.templates.findIndex((item) => item.id === left.templateId) -
                    chart.data.templates.findIndex((item) => item.id === right.templateId),
                )
                .map((record) => {
                  const template = chart.data.templates.find(
                    (item) => item.id === record.templateId,
                  )!;
                  const current = record.versions.at(-1)!;
                  return (
                    <div
                      key={record.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-surface-border px-3 py-2"
                    >
                      <span className="text-sm">
                        <strong>{template.displayName}</strong>{' '}
                        <span className="text-xs text-content-muted">
                          versão {current.version} · {formatDate(current.recordedAt, timeZone)}
                        </span>
                      </span>
                      <IconButton
                        label="Abrir"
                        icon={<Eye />}
                        onClick={() => onOpenChart(record.templateId)}
                      />
                    </div>
                  );
                })
            ) : (
              <p className="text-sm text-content-faint">Nenhuma ficha preenchida.</p>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}
